import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { UiService } from 'src/app/services/ui.service'
import { ApiService } from 'src/app/services/api.service'
import { ViewableIssue } from 'src/app/account/components/issues-view/issues-view.component'
import { SyncPair, SyncPairResourceConfig } from 'src/app/account/services/sync-pair.service'
import { UntypedFormBuilder } from '@angular/forms'
import { AccountService } from 'src/app/account/services/account.service'
import { getResourceTypeDisplayString } from 'src/app/shared/utils'
import { ResourceType } from 'src/utils/resource-types'
import { ResourceConfigDto, SyncPairIssueScopeDto } from 'src/app/services/dtos'
import { finalize, switchMap } from 'rxjs/operators'
import { handleArpioError } from 'src/utils/handle-error'
import { Subscription } from 'rxjs'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { Application } from 'src/app/account/services/application.service'

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'failback-resource-issue',
  templateUrl: './failback-resource-issue.component.html',
  styleUrls: ['./failback-resource-issue.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FailbackResourceIssueComponent implements OnInit {
  @Input() issue: ViewableIssue = null
  // tslint:disable-next-line:no-input-rename
  @Input('sync-pair') syncPair: SyncPair = null
  @Input('application') application: Application = null

  failbackResourceFormModel = this.formBuilder.group({
    newResourceId: [''],
    useNewResource: ['true'],
  })
  private savingSubscription: Subscription = null

  constructor(
    public uiService: UiService,
    public apiService: ApiService,
    public accountService: AccountService,
    private formBuilder: UntypedFormBuilder
  ) {}

  setInputs(issue: ViewableIssue, syncPair: SyncPair) {
    this.issue = issue
    this.syncPair = syncPair
  }

  ngOnInit(): void {}

  submitForm() {
    if (this.useNewResource) {
      const config = {
        resourceConfigType: ResourceType.ec2Instance.valueOf(),
        failbackConfig: {
          instanceId: this.failbackResourceFormModel.value.newResourceId,
        },
        // failbackInstanceId: this.failbackResourceFormModel.value.newResourceId,
      } as ResourceConfigDto

      let saver$ = null
      if (this.syncPair) {
        saver$ = this.syncPair.setSyncPairResourceConfig$(this.issue.arn, config)
      } else {
        saver$ = this.application.setSyncPairResourceConfig$(this.issue.arn, config)
      }
      this.savingSubscription = saver$
        .pipe(
          switchMap((returnedConfig: SyncPairResourceConfig) => {
            console.log(`Saved config: ${returnedConfig}`)
            return this.issue.deleter$()
          }),
          finalize(() => (this.savingSubscription = null))
        )
        .subscribe(
          (response) => {
            console.log(`Success response ${response}`)
          },
          handleArpioError(window.location.href, this.apiService, this.uiService)
        )
    }
  }

  get useNewResource(): boolean {
    return this.failbackResourceFormModel.value.useNewResource === 'true'
  }

  get resourceName() {
    return !!this.issue.resourceName ? this.issue.resourceName : 'unknown name'
  }

  get resourceTypeString(): string {
    if (!!this.issue && this.issue.resourceType) {
      return getResourceTypeDisplayString(this.issue.resourceType, this.issue.resourceVariant)
    } else {
      return ''
    }
  }

  get saving(): boolean {
    return subscriptionActive(this.savingSubscription)
  }

  get formIsValid(): boolean {
    if (this.useNewResource) {
      return this.failbackResourceFormModel.value.newResourceId.length > 0
    } else {
      return true
    }
  }

  get notReadyToSave(): boolean {
    return this.saving || !this.formIsValid
  }

  get showIdHint(): boolean {
    if (this.issue.resourceType === ResourceType.ec2Instance && this.formIsValid) {
      const id = this.failbackResourceFormModel.value.newResourceId
      // Valid instance IDs are i-0123abcd (2006-2018) or i-0123456789abcdef0 (2016-present)
      // https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/resource-ids.html
      if (!id.startsWith('i-') || (id.length !== 10 && id.length !== 19)) {
        return true
      }
      for (const ch of id.slice(2)) {
        if (!'0123456789abcdef'.includes(ch)) {
          return true
        }
      }
    }
    return false
  }

  get sourceRegion() {
    return !!this.syncPair ? this.syncPair.sourceRegion : this.application.sourceRegion
  }

  get sourceAwsAccountId() {
    return !!this.syncPair ? this.syncPair.sourceAwsAccountId : this.application.sourceAwsAccountId
  }

  get targetRegion() {
    return !!this.syncPair ? this.syncPair.targetRegion : this.application.targetRegion
  }

  get targetAwsAccountId() {
    return !!this.syncPair ? this.syncPair.targetAwsAccountId : this.application.targetAwsAccountId
  }
}
